import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SalesTargetsService } from 'src/app/services/sales-targets/sales-targets.service';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';

// Declare jQuery to avoid TypeScript compilation errors
declare var $: any;

@Component({
  selector: 'app-sales-targets',
  templateUrl: './sales-targets.component.html',
  styleUrls: ['./sales-targets.component.css']
})
export class SalesTargetsComponent implements OnInit{
  page: number = 1;
  count: number = 0;
  searchText: string = '';
  sales_targets: any[] = [];
  public errorMessage: string | null = null;
  public successMessage: string | null = null;
  public isLoading: boolean = false;

  addTargetForm!: FormGroup;
  updateTargetForm!: FormGroup;
  submitted: boolean = false;
  salesReps: any[] = [];
  selectedSalesRep!: string;
  isProcessing: boolean = false;

  // Edit sale target
  saleTarget: any;
  saleRepId: string | null = null;
  months: { currOrNextMon: string}[] = [];

  constructor(
    private salesTargetsService: SalesTargetsService,
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
  ){
    this.addTargetForm = this.formBuilder.group({
      target_units:['', Validators.required],
    });

    this.updateTargetForm = this.formBuilder.group({
      target_units:['', Validators.required],
      month: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.isLoading=true;
    this.fetchSalesTargets();
    this.generateMonthOptions();
  }

  fetchSalesTargets():void{
    this.salesTargetsService.getSalesTargets().subscribe({
      next: (response) => {
        if(response.status==='success'){
          this.sales_targets = response.data
          this.isLoading=false;
        }else{
          this.errorMessage=response.message;
          this.isLoading=false;
        }
      },
      error: (error) => {
        this.errorMessage=error;
        this.isLoading=false;
      }
    });
  }

  pageChangeEvent(event: number){
    this.page = event;
    this.fetchSalesTargets();
  }

  onSearchTextChange(event: Event) {
    this.searchText = (event.target as HTMLInputElement).value;
  }

  delete(event: Event,salesRepId:string){
    event.preventDefault();
    this.confirmBox(salesRepId);
  }

  confirmBox(salesRepId: string){
    Swal.fire({
      title: 'Do you really want to delete this sale target?',
      text: 'All sale target histories associated with this record will be parmanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes!',
      cancelButtonText: 'No'
    }).then((result) => {
      if (result.value) {
        this.isProcessing=true;

        this.salesTargetsService.delete(salesRepId)
        .subscribe({
          next: (response) => {
            if(response.status==='success'){
              this.isProcessing=false;
              this.toastr.success(response.message, 'Success!');
              this.errorMessage=''; //Clear error message incase it's available

              //Reload sale targets
              this.fetchSalesTargets();
            }else if(response.status==='error'){
              this.isProcessing=false;
              this.toastr.error(response.message, 'Error!');
            }else{
              this.isProcessing=false;
              this.toastr.error(response.message, 'Error!');
            }
          },
          error: (error) => {
            this.isProcessing=false;
            this.toastr.error(error, 'Errror!');
          }
        });
      } else if (result.dismiss === Swal.DismissReason.cancel) {

      }
    })
  }

  openAddSalesTargetModal(): void{
    this.salesTargetsService.getSalesReps().subscribe({
      next: (response) => {
        if(response.status==='success'){
          this.isLoading = false;
          this.salesReps = response.data
          $('#addSalesTargetModal').modal('show');
        }else{
          this.isLoading = false;
          this.errorMessage = response.message;
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = error;
      }
    });
  }

  addSalesTarget(): void{
    this.submitted = true;
    this.isProcessing = true;

    if(this.addTargetForm.valid) {
      if(this.selectedSalesRep==null || this.selectedSalesRep==undefined || this.selectedSalesRep==''){
        this.errorMessage="Please select a sales rep. to proceed!";
        this.isProcessing = false;
      }else{
        this.salesTargetsService.store(this.selectedSalesRep,this.addTargetForm.value.target_units)
        .subscribe({
          next: (response) => {
            if(response.status==='success'){
              this.submitted = false;
              this.isProcessing = false;
              this.errorMessage=null;
              this.toastr.success(response.message, 'Success!');
              this.selectedSalesRep = '';
              this.addTargetForm.reset();
              $('#addSalesTargetModal').modal('hide');
              //Refresh sales targets list
              this.isLoading=true;
             this.fetchSalesTargets();
            }else{
              this.toastr.error(response.message, 'Error!');
              this.submitted = false;
              this.isProcessing = false;
            }
          },
          error: (error) => {
            this.errorMessage=error;
            this.toastr.error(error, 'Error!');
            this.submitted = false;
            this.isProcessing = false;
          }
        });
      }
    }else{
      this.isProcessing = false;
    }
  }

  getSaleTargetInfo(event: Event,id: string){
    event.preventDefault();

     this.salesTargetsService.edit(id).subscribe({
      next: (response) => {
        if(response.status==='success'){
          this.isLoading = false;
          this.saleTarget = response.data

          //Track sales_rep_id for use in updating
          this.saleRepId = this.saleTarget.sales_rep_id;

          // Populate the reactive form
          this.updateTargetForm.patchValue({
            target_units: this.saleTarget.target_units
          });

          $('#editSalesTargetModal').modal('show');
        }else{
          this.isLoading = false;
          this.errorMessage = response.message;
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = error;
      }
    });
  }

  /**
   * Generate current and next month for
   * the Month dropdown
   */
  generateMonthOptions(): void {
    const currentDate = new Date();

    this.months = [];

    for (let i = 0; i < 2; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + i,
        1
      );

      const month = date.toLocaleDateString('en-US', {
        month: 'short'
      });

      const year = date.getFullYear();

      this.months.push({
        currOrNextMon: `${month}-${year}`
      });
    }
  }

  updateSalesTarget(): void{
    this.submitted = true;
    this.isProcessing = true;

    if(this.updateTargetForm.valid) {
      if(this.saleRepId==null || this.saleRepId==undefined || this.saleRepId==''){
         this.toastr.error("Unable to update sale target, please close this pop-up and try again", 'Error!');
        this.isProcessing = false;
      }else{
        this.salesTargetsService.update(this.saleRepId,this.updateTargetForm.value.target_units,this.updateTargetForm.value.month)
        .subscribe({
          next: (response) => {
            if(response.status==='success'){
              this.submitted = false;
              this.isProcessing = false;
              this.errorMessage=null;
              this.toastr.success(response.message, 'Success!');
              this.saleRepId = '';
              this.updateTargetForm.reset();
              $('#editSalesTargetModal').modal('hide');

              //Refresh sales targets list
              this.isLoading=true;
             this.fetchSalesTargets();
            }else{
              this.toastr.error(response.message, 'Error!');
              this.submitted = false;
              this.isProcessing = false;
            }
          },
          error: (error) => {
            this.errorMessage=error;
            this.toastr.error(error, 'Error!');
            this.submitted = false;
            this.isProcessing = false;
          }
        });
      }
    }else{
      this.isProcessing = false;
    }
  }
}
